<script setup lang="ts">
import {computed, onMounted, ref, shallowRef} from "vue";
import { useRouter } from "vue-router";
import { AgGridVue } from "ag-grid-vue3";
import {
  type CellValueChangedEvent,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import CharterFlightActions from "@/charter/components/CharterFlightActions.vue";
import {useCharterFlightsStore} from "@/charter/stores/charter-flights-store.ts";
import {columnTypes} from "@/app/ag-grid/ag-grid-column-types.ts";
import {dateFilterParams} from "@/app/ag-grid/ag-grid-filters.ts";
import GridRouteFilter from "@/components/Allotment/GridRouteFilter.vue";
import type {Charter, CharterVipUpdateRequest} from "@/api/client/models/index.ts";
import {
  formatNetlineDate,
  formatNetlineFileTimestamp,
  formatNetlineTime,
  formatNetlineWeekdayPattern,
  NETLINE_COLUMN_IDS,
  NETLINE_CONFIGURATION_CODE,
  NETLINE_HEADERS,
} from "@/charter/netline-export.ts";

const charterFlightsStore = useCharterFlightsStore();
const confirm = useConfirm();
const toast = useToast();
const router = useRouter();

const showArchived = ref(false);

const charterFlightColumnDefs: GridOptions["columnDefs"] = [
  {
    field: "isVip",
    headerName: "VIP",
    editable: () => !showArchived.value,
    singleClickEdit: true,
    cellDataType: "boolean",
    cellEditor: "agCheckboxCellEditor",
    cellRenderer: "agCheckboxCellRenderer",
    filter: true,
  },
  { field: "departureDate", headerName: "Date", type: "dateColumn" },
  { field: "season", filter: true },
  { field: "snapshot", filter: true },
  { field: "carrier", headerName: "Aln", type: "textColumn" },
  { field: "flightNumber", headerName: "Flt", type: "textColumn" },
  { field: "opSuffix", headerName: "Suffix", type: "textColumn" },
  { field: "aircraftType", headerName: "Ac", type: "textColumn" },
  { field: "aircraftOwner", headerName: "Aircraft owner", type: "textColumn" },
  { field: "origin", headerName: "From", type: "textColumn" },
  { field: "std", filterParams: dateFilterParams, type: "dateTimeColumn" },
  { field: "destination", headerName: "To", type: "textColumn" },
  { field: "sta", filterParams: dateFilterParams, type: "dateTimeColumn" },
  { field: "flightStatus", headerName: "Status", filter: true },
  { field: "client", headerName: "Client detail", filter: true },
  { field: "flightType", headerName: "Flt type", type: "textColumn" },
  { field: "ferryOrLive", headerName: "Live / Ferry", type: "textColumn" },
  { field: "revenueNet", type: "numberColumn" },
  { field: "revenueTtl", type: "numberColumn" },
  { field: "vardocTtl", type: "numberColumn" },
  { field: "fixDocTtl", type: "numberColumn" },
  { field: "docTtl", type: "numberColumn" },
  { field: "db1", type: "textColumn" },
  { field: "fltNr", type: "textColumn" },
  { field: "blockTime", type: "numberColumn" },
  { field: "btDec", type: "numberColumn" },
  { field: "seats", type: "numberColumn" },
  { field: "legs", filter: true },
  { field: "stretch", filter: GridRouteFilter },
  { field: "route", filter: GridRouteFilter },
  { field: "country", type: "textColumn" },
  { field: "year", type: "numberColumn" },
  { field: "month", type: "numberColumn" },
  { field: "fltnrRoute", headerName: "Flt Nr Router", filter: true },
  { field: "flownPax", type: "numberColumn" },
  { field: "linkedFlights", headerName: "Linked flt", type: "textColumn" },
  {
    colId: "netlineCarrier",
    headerName: NETLINE_HEADERS.netlineCarrier,
    hide: true,
    valueGetter: (params) => params.data?.carrier ?? "",
  },
  {
    colId: "netlineFlightNumber",
    headerName: NETLINE_HEADERS.netlineFlightNumber,
    hide: true,
    valueGetter: (params) => params.data?.flightNumber ?? "",
  },
  {
    colId: "netlineOpSuffix",
    headerName: NETLINE_HEADERS.netlineOpSuffix,
    hide: true,
    valueGetter: (params) => params.data?.opSuffix ?? "",
  },
  {
    colId: "netlineStartDate",
    headerName: NETLINE_HEADERS.netlineStartDate,
    hide: true,
    valueGetter: (params) => formatNetlineDate(params.data?.departureDate),
  },
  {
    colId: "netlineEndDate",
    headerName: NETLINE_HEADERS.netlineEndDate,
    hide: true,
    valueGetter: (params) => formatNetlineDate(params.data?.departureDate),
  },
  {
    colId: "netlineWeekdayPattern",
    headerName: NETLINE_HEADERS.netlineWeekdayPattern,
    hide: true,
    valueGetter: (params) => formatNetlineWeekdayPattern(params.data?.departureDate),
  },
  {
    colId: "netlineOrigin",
    headerName: NETLINE_HEADERS.netlineOrigin,
    hide: true,
    valueGetter: (params) => params.data?.origin ?? "",
  },
  {
    colId: "netlineStd",
    headerName: NETLINE_HEADERS.netlineStd,
    hide: true,
    valueGetter: (params) => formatNetlineTime(params.data?.std),
  },
  {
    colId: "netlineSta",
    headerName: NETLINE_HEADERS.netlineSta,
    hide: true,
    valueGetter: (params) => formatNetlineTime(params.data?.sta),
  },
  {
    colId: "netlineDestination",
    headerName: NETLINE_HEADERS.netlineDestination,
    hide: true,
    valueGetter: (params) => params.data?.destination ?? "",
  },
  {
    colId: "netlineAircraftOwner",
    headerName: NETLINE_HEADERS.netlineAircraftOwner,
    hide: true,
    valueGetter: (params) => params.data?.aircraftOwner ?? "",
  },
  {
    colId: "netlineAircraftType",
    headerName: NETLINE_HEADERS.netlineAircraftType,
    hide: true,
    valueGetter: (params) => params.data?.aircraftType ?? "",
  },
  {
    colId: "netlineConfigurationCode",
    headerName: NETLINE_HEADERS.netlineConfigurationCode,
    hide: true,
    valueGetter: () => NETLINE_CONFIGURATION_CODE,
  },
  {
    colId: "netlineServiceType",
    headerName: NETLINE_HEADERS.netlineServiceType,
    hide: true,
    valueGetter: (params) => params.data?.serviceType ?? "",
  },
];

const actionColumnDef: NonNullable<GridOptions["columnDefs"]>[number] = {
  headerName: "",
  filter: false,
  type: "actionColumn",
  cellRenderer: CharterFlightActions,
  cellRendererParams: {
    onArchive: (id: number) =>
      showConfirm("Are you sure you want to archive this flight?", "Archive flight", () =>
        archiveFlight(id)
      ),
    onCrewBriefing: (id: number) => openCrewBriefing(id),
  },
};

const columnDefs = computed(() =>
  showArchived.value ? charterFlightColumnDefs : [...charterFlightColumnDefs, actionColumnDef]
);

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitCellContents",
  },
  defaultColDef: {
    resizable: false,
  },
  columnTypes: columnTypes
};

const gridApi = shallowRef<GridApi | null>(null);

onMounted(async () => {
  await charterFlightsStore.refresh();
});

function showConfirm(
  message: string,
  title: string,
  onAccept: () => Promise<void>
): void {
  confirm.require({
    message: message,
    header: title,
    // rejectProps: {
    //     label: rejectLabel,
    //     severity: 'secondary',
    //     outlined: true
    // },
    // acceptProps: {
    //     label: acceptLabel
    // },
    accept: onAccept,
  });
}

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

async function onCellValueChanged(event: CellValueChangedEvent<Charter>): Promise<void> {
  if (
    showArchived.value ||
    event.colDef.field !== "isVip" ||
    event.newValue === event.oldValue ||
    event.data?.id == null
  ) {
    return;
  }

  const request: CharterVipUpdateRequest = {
    id: event.data.id,
    version: event.data.version,
    isVip: event.data.isVip ?? false,
  };

  try {
    await charterFlightsStore.updateCharterVip(request);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'An error occurred',
      detail: error instanceof Error ? error.message : 'Charter flight VIP status could not be updated',
      life: 5000,
    });
  } finally {
    await refreshData();
  }
}

function exportData() {
  if (!gridApi.value) {
    return;
  }

  gridApi.value.exportDataAsExcel({
    columnKeys: [...NETLINE_COLUMN_IDS],
    fileName: `netline-charters-${formatNetlineFileTimestamp()}.xlsx`,
    sheetName: "NetLine",
  });
}

async function refreshData() {
  await charterFlightsStore.refresh(showArchived.value);
}

async function archiveFlight(id: number): Promise<void> {
  return charterFlightsStore.archiveCharterFlight(id)
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Charter flight archived successfully',
        life: 3000,
      });
    })
    .then(() => refreshData())
    .catch((error) => {
      toast.add({
        severity: 'error',
        summary: 'An error occurred',
        detail: error instanceof Error ? error.message : 'Charter flight could not be archived',
        life: 5000,
      });
    });
}

function openCrewBriefing(id: number): void {
  void router.push({
    name: 'charter-flight-crew-briefing',
    params: { charterId: id },
  });
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>Charter flights</h1>
      <div class="flex items-center gap-2">
        <p-input-switch v-model="showArchived" @change="refreshData" :disabled="charterFlightsStore.isLoading" />
        <label class="mr-2">Show archived</label>
        <p-button class="px-4" link :loading="charterFlightsStore.isLoading" @click="refreshData">
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
        <p-button class="px-4" label="Download" link @click="exportData" :disabled="charterFlightsStore.isLoading">
          <span class="material-icons" title="download">download</span>
        </p-button>
      </div>
    </div>

    <AgGridVue
      :rowData="charterFlightsStore.charterFlights"
      :loading="charterFlightsStore.isLoading"
      :gridOptions="gridOptions"
      :columnDefs="columnDefs"
      ref="gridRef"
      style="width: 100%; height: 100%"
      @cell-value-changed="onCellValueChanged"
      @grid-ready="onGridReady"
    />
  </div>
</template>
